import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import { Button, FormGroup, Input, Modal, ModalContent, ModalTitle } from "@lib/components";
import { withTranslation, WithTranslation } from "react-i18next";
import { Formik, FormikActions } from "formik";
import moment from "moment-timezone";
import { FORMATS, Untrusive } from "@lib/common";
import isEmail from "validator/lib/isEmail";
import { UI } from "../../../../../core/ui";
import styled from "styled-components";

interface Props extends WithTranslation {
	open: boolean;
	onClose: () => void;
	onCreated: (booking: T.Schema.Booking.BookingSchema) => void;
}

interface FormValues {
	name: string;
	email: string;
	phone: string;
	number_of_people: number;
	date: string;
	time: string;
	notes: string;
}

const ModalSubtitle = styled.p`
	margin: 0.35rem 0 0;
	font-size: 0.75rem;
	font-weight: 700;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.14em;
`;

const SummaryGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
	padding: 1.25rem;
	margin-bottom: 1.25rem;
	background: #f8fafc;
	border-radius: 1.25rem;
	border: 1px solid #f1f5f9;
`;

const SummaryLabel = styled.span`
	display: block;
	font-size: 0.625rem;
	font-weight: 800;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.12em;
	margin-bottom: 0.25rem;
`;

const SummaryValue = styled.span`
	font-size: 1rem;
	font-weight: 800;
	color: #4f46e5;
`;

const FieldLabel = styled.label`
	display: block;
	font-size: 0.625rem;
	font-weight: 800;
	color: #64748b;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	margin: 0 0 0.35rem 0.15rem;
`;

const StyledInput = styled(Input)`
	&& {
		border-radius: 1rem;
		border: 2px solid #f1f5f9;
		padding: 0.65rem 0.85rem;
		font-weight: 600;
		font-size: 0.9rem;
		transition: border-color 0.15s ease, box-shadow 0.15s ease;
	}
	&&:focus {
		border-color: #a5b4fc;
		box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
	}
`;

const StyledTextarea = styled.textarea`
	width: 100%;
	border-radius: 1rem;
	border: 2px solid #f1f5f9;
	padding: 0.75rem 0.85rem;
	font-weight: 600;
	font-size: 0.875rem;
	resize: vertical;
	min-height: 88px;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
	&:focus {
		outline: none;
		border-color: #a5b4fc;
		box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
	}
`;

const FormGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
	@media (max-width: 520px) {
		grid-template-columns: 1fr;
	}
`;

const ActionsRow = styled.div`
	display: flex;
	gap: 0.65rem;
	margin-top: 1.5rem;
`;

@inject("store")
@observer
export class RestaurantBookingCreateModalClass extends MobxComponent<Props> {
	render() {
		const { store, t, open, onClose, onCreated } = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const service = r.settings.services.table_booking;
		const maxPeople = service.max_people || service.limit_number_of_people;
		const minPeople = service.min_people;

		const defaultDate = moment().tz(tz).format(FORMATS.moment.date);

		const initial: FormValues = {
			name: "",
			email: "",
			phone: "",
			number_of_people: Math.min(
				maxPeople && maxPeople > 0 ? maxPeople : 8,
				4
			),
			date: defaultDate,
			time: "18:00",
			notes: "",
		};

		const submit = async (v: FormValues, form: FormikActions<FormValues>) => {
			let err = "";
			if (!v.name.trim()) err = "required_name";
			if (!v.email.trim()) err = "required_email";
			else if (!isEmail(v.email, { require_tld: true })) err = "invalid_email";
			if (!v.phone.trim()) err = "required_phone";
			if (maxPeople || minPeople) {
				if (
					maxPeople &&
					minPeople &&
					(v.number_of_people > maxPeople || v.number_of_people < minPeople)
				) {
					err = "invalid_people";
				} else if (maxPeople && !minPeople && v.number_of_people > maxPeople) {
					err = "invalid_people";
				} else if (!maxPeople && minPeople && v.number_of_people < minPeople) {
					err = "invalid_people";
				}
			}
			if (err) {
				UI.notification.error(t(`booking.error.${err}`, { defaultValue: err }));
				form.setSubmitting(false);
				return;
			}

			const timestamp = moment
				.tz(`${v.date} ${v.time}`, FORMATS.moment.datetime, tz)
				.valueOf();

			Untrusive.start();
			form.setSubmitting(true);
			try {
				const res = await store.api.booking_create({
					restaurant_id: r._id,
					booking: {
            restaurantID: r._id,
						notes: v.notes.trim(),
						custom_booking_field_list: [],
						customer: {
							_id: "",
							name: v.name.trim(),
							phone: v.phone.trim(),
							email: v.email.trim(),
						},
						config: {
							date: moment(timestamp).tz(tz).format(FORMATS.moment.date),
							time: moment(timestamp).tz(tz).format(FORMATS.moment.time),
							timestamp,
							number_of_people: v.number_of_people,
						},
					},
				});
				if (res.outcome !== 0) {
					throw new Error((res as { message?: string }).message || "create_failed");
				}
				if ("booking" in res && res.booking) {
					onCreated(res.booking);
				}
				UI.notification.success(
					t("Booking created", { defaultValue: "Booking created" })
				);
				onClose();
			} catch {
				UI.notification.error(
					t("Could not create booking", { defaultValue: "Could not create booking" })
				);
			} finally {
				form.setSubmitting(false);
				Untrusive.stop();
			}
		};

		return (
			<Modal active={open} close={onClose} width="md" alignTop>
				<ModalTitle className="round-top">
					{t("Add booking", { defaultValue: "Add booking" })}
				</ModalTitle>
				<ModalContent paddinglr={24} paddingtb={20}>
					<ModalSubtitle>
						{t("bookingCreate.subtitle", {
							defaultValue: "Table reservation",
						})}
					</ModalSubtitle>
					<Formik initialValues={initial} onSubmit={submit} enableReinitialize>
						{({ values, handleChange, handleSubmit, isSubmitting }) => {
							const previewTs = moment
								.tz(
									`${values.date} ${values.time}`,
									FORMATS.moment.datetime,
									tz
								)
								.valueOf();
							return (
								<form onSubmit={handleSubmit}>
									<SummaryGrid>
										<div>
											<SummaryLabel>
												{t("Reservation time", {
													defaultValue: "Reservation time",
												})}
											</SummaryLabel>
											<SummaryValue>
												{t("datetimeFromTimestamp", { value: previewTs })}
											</SummaryValue>
										</div>
										<div>
											<SummaryLabel>
												{t("Party size", { defaultValue: "Party size" })}
											</SummaryLabel>
											<SummaryValue>
												{t("number", { value: values.number_of_people })}{" "}
												{t("guests", { defaultValue: "guests" })}
											</SummaryValue>
										</div>
									</SummaryGrid>

									<FormGroup>
										<FieldLabel htmlFor="booking-create-name">
											{t("Guest name", { defaultValue: "Guest name" })}
										</FieldLabel>
										<StyledInput
											id="booking-create-name"
											name="name"
											value={values.name}
											onChange={handleChange}
											autoComplete="name"
											required
										/>
									</FormGroup>
									<FormGrid>
										<FormGroup>
											<FieldLabel htmlFor="booking-create-email">
												{t("Email", { defaultValue: "Email" })}
											</FieldLabel>
											<StyledInput
												id="booking-create-email"
												name="email"
												type="email"
												value={values.email}
												onChange={handleChange}
												autoComplete="email"
												required
											/>
										</FormGroup>
										<FormGroup>
											<FieldLabel htmlFor="booking-create-phone">
												{t("Phone", { defaultValue: "Phone" })}
											</FieldLabel>
											<StyledInput
												id="booking-create-phone"
												name="phone"
												value={values.phone}
												onChange={handleChange}
												autoComplete="tel"
												required
											/>
										</FormGroup>
									</FormGrid>
									<FormGrid>
										<FormGroup>
											<FieldLabel htmlFor="booking-create-date">
												{t("Date", { defaultValue: "Date" })}
											</FieldLabel>
											<StyledInput
												id="booking-create-date"
												name="date"
												type="date"
												value={values.date}
												onChange={handleChange}
												required
											/>
										</FormGroup>
										<FormGroup>
											<FieldLabel htmlFor="booking-create-time">
												{t("Time", { defaultValue: "Time" })}
											</FieldLabel>
											<StyledInput
												id="booking-create-time"
												name="time"
												type="time"
												value={values.time}
												onChange={handleChange}
												required
											/>
										</FormGroup>
									</FormGrid>
									<FormGroup>
										<FieldLabel htmlFor="booking-create-party">
											{t("Party size", { defaultValue: "Party size" })}
										</FieldLabel>
										<StyledInput
											id="booking-create-party"
											name="number_of_people"
											type="number"
											min={minPeople || 1}
											max={maxPeople || 99}
											value={values.number_of_people}
											onChange={handleChange}
											required
										/>
									</FormGroup>
									<FormGroup>
										<FieldLabel htmlFor="booking-create-notes">
											{t("Notes", { defaultValue: "Notes" })}
										</FieldLabel>
										<StyledTextarea
											id="booking-create-notes"
											name="notes"
											value={values.notes}
											onChange={handleChange}
											placeholder={t("Optional notes", {
												defaultValue: "Optional notes",
											})}
										/>
									</FormGroup>
									<ActionsRow>
										<Button
											type="button"
											color="white"
											full
											onClick={onClose}
											disabled={isSubmitting}
											style={{
												borderRadius: "1rem",
												border: "2px solid #f1f5f9",
												fontWeight: 800,
												fontSize: "0.75rem",
												textTransform: "uppercase",
												letterSpacing: "0.08em",
												color: "#64748b",
											}}
										>
											{t("Cancel", { defaultValue: "Cancel" })}
										</Button>
										<Button
											type="submit"
											color="primary"
											full
											disabled={isSubmitting}
											style={{
												borderRadius: "1rem",
												fontWeight: 800,
												fontSize: "0.75rem",
												textTransform: "uppercase",
												letterSpacing: "0.08em",
											}}
										>
											{t("Create booking", { defaultValue: "Create booking" })}
										</Button>
									</ActionsRow>
								</form>
							);
						}}
					</Formik>
				</ModalContent>
			</Modal>
		);
	}
}

export const RestaurantBookingCreateModal = withTranslation()(
	RestaurantBookingCreateModalClass
);
